import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../../assets/Logo.png";

const AuthHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [atTop, setAtTop] = useState(true);

  useEffect(() => {
    const onScroll = () => setAtTop(window.scrollY < 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header role="banner" className="sticky top-0 z-50">
      {/* thin brand rail (light) */}
      <div className="h-[2px] w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500/90" />

      {/* main bar (light glass like main header) */}
      <div
        className={[
          "backdrop-blur-xl transition-all duration-300 border-b",
          atTop
            ? "bg-white/60 border-transparent"
            : "bg-white/85 border-slate-200 shadow-[0_8px_30px_-12px_rgba(2,6,23,.18)]",
        ].join(" ")}
      >
        <div className="max-w-7xl mx-auto h-16 px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* logo */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-md"
            aria-label="Go home"
          >
            {/* <img src={Logo} alt="Roboman" className="h-6 w-auto" /> */}
            <span className="sr-only">OUTREACH</span>
          </button>

          {/* desktop nav (light text) */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Auth navigation">
            {[
              { label: "Login", path: "/login" },
              { label: "Sign Up", path: "/signup" },
              { label: "Forgot Password", path: "/forgotpw" },
            ].map(({ label, path }) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                className={[
                  "relative inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-semibold",
                  "text-slate-700 hover:text-slate-900",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                ].join(" ")}
              >
                {/* pill bg on hover */}
                <span className="absolute inset-0 rounded-full bg-slate-900/0 hover:bg-slate-900/[.04] transition" />
                <span className="relative z-10">{label}</span>
                {/* active underline (light gradient) */}
                <span
                  className={[
                    "absolute left-4 right-4 -bottom-[2px] h-[2px] rounded-full",
                    isActive(path)
                      ? "bg-gradient-to-r from-indigo-500 to-fuchsia-500"
                      : "bg-transparent",
                  ].join(" ")}
                />
              </button>
            ))}
          </nav>

          {/* actions (desktop) */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => navigate("/signup")}
              className="relative inline-flex items-center justify-center overflow-hidden rounded-full px-5 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600" />
              <span className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity bg-[radial-gradient(100%_100%_at_50%_0%,rgba(255,255,255,.35)_0,rgba(255,255,255,0)_60%)]" />
              <span className="relative">Sign Up Free</span>
            </button>
          </div>

          {/* mobile toggle (light ring/text) */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg ring-1 ring-slate-300 text-slate-700 hover:bg-white/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {open ? (
                <path d="M18 6 6 18M6 6l12 12" />
              ) : (
                // use <g> instead of fragment to avoid JSX-in-SVG syntax issues
                <g>
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </g>
              )}
            </svg>
          </button>
        </div>

        {/* mobile sheet (light card) */}

        {open && (
          <div
            className="md:hidden transition-all duration-300"
            aria-hidden={!open}
          >

            <div className="px-4 pb-4">

              <div className="mt-2 rounded-2xl bg-white/92 backdrop-blur-xl ring-1 ring-slate-200 shadow-xl p-4">
                <nav className="grid gap-2" aria-label="Mobile auth navigation">
                  {[
                    { label: "Login", path: "/login" },
                    { label: "Sign Up", path: "/signup" },
                    { label: "Forgot Password", path: "/forgotpw" },
                  ].map(({ label, path }) => (
                    <button
                      key={path}
                      onClick={() => {
                        setOpen(false);
                        navigate(path);
                      }}
                      className={[
                        "text-base px-4 py-3 text-left rounded-xl ring-1 ring-slate-200 bg-white hover:bg-slate-50",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                        isActive(path) ? "text-indigo-600" : "text-slate-800",
                      ].join(" ")}
                    >
                      {label}
                    </button>
                  ))}
                </nav>

                <div className="mt-3">
                  <button
                    onClick={() => {
                      setOpen(false);
                      navigate("/signup");
                    }}
                    className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  >
                    Sign Up Free
                  </button>
                </div>

              </div>

            </div>

          </div>
        )}
      </div>
    </header>
  );
};

export default AuthHeader;
